import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarDays,
  LayoutDashboard,
  Megaphone,
  Plus,
  User,
} from 'lucide-react';
import { useUiStore } from '@/stores/ui-store';
import { useModalOpen } from '@/hooks/use-modal-open';
import { cn } from '@/lib/utils';

/* Abas de navegação (4) — o FAB central fica na coluna do meio do grid */
type NavItem = {
  to: '/dashboard' | '/calendar' | '/marketing' | '/settings';
  label: string;
  icon: typeof LayoutDashboard;
};

const items: NavItem[] = [
  { to: '/dashboard', label: 'Painel', icon: LayoutDashboard },
  { to: '/calendar', label: 'Eventos', icon: CalendarDays },
  { to: '/marketing', label: 'Posts', icon: Megaphone },
  { to: '/settings', label: 'Conta', icon: User },
];

export function BottomNav() {
  const { pathname } = useLocation();
  const openCreate = useUiStore((s) => s.openCreate);
  const modalOpen = useModalOpen();

  /* O FAB ocupa a coluna central; as 4 abas se dividem 2 à esquerda e 2 à direita */
  const left = items.slice(0, 2);
  const right = items.slice(2);

  return (
    <nav
      aria-label="Navegação inferior"
      aria-hidden={modalOpen}
      className={cn(
        'pb-[max(0px,env(safe-area-inset-bottom))] lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-background transition-opacity duration-200',
        // Some sob modais: o backdrop translúcido deixava a barra near-white vazar como retângulo.
        modalOpen && 'pointer-events-none opacity-0',
      )}
    >
      <div className="grid h-[48px] w-full grid-cols-[1fr_1fr_64px_1fr_1fr] px-1">
        {left.map((item) => (
          <Tab key={item.to} item={item} active={pathname.startsWith(item.to)} />
        ))}

        {/* FAB central — abre o sheet de criação */}
        <div className="flex items-center justify-center">
          <motion.button
            type="button"
            onClick={openCreate}
            whileTap={{ scale: 0.92 }}
            aria-label="Criar"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-red active:scale-[0.92] transition"
          >
            <Plus className="h-[22px] w-[22px]" strokeWidth={2.5} />
          </motion.button>
        </div>

        {right.map((item) => (
          <Tab key={item.to} item={item} active={pathname.startsWith(item.to)} />
        ))}
      </div>
    </nav>
  );
}

/* Aba individual — ativo sinalizado por cor, peso da fonte e espessura do traço */
function Tab({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      className={cn(
        'relative flex h-full flex-col items-center justify-center gap-1 px-0 transition-colors',
        active ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={active ? 2.2 : 1.75} />
      <span
        className={cn(
          'font-mono text-[9.5px] uppercase tracking-[0.08em]',
          active ? 'font-semibold' : 'font-medium',
        )}
      >
        {item.label}
      </span>
    </NavLink>
  );
}
